/**
 * What the studio calls each background worker, and what actually stops
 * working when one does.
 *
 * Split out of utils/workerHealth.ts on 6 Oct 2026 so that adding a worker is
 * a two-line change to a small file rather than a rewrite of the health logic
 * around it. Nothing else moved.
 *
 * The consequence line is the point of this file. A dashboard row saying
 * "calendar_sync is late" tells nobody what to do about it; a row saying what
 * has quietly stopped being true does. The two calendar workers fail in
 * opposite directions and are worth telling apart for exactly that reason:
 * one stops Google reaching Portal OS, the other stops Portal OS reaching
 * Google.
 */

/** Anything unregistered falls back to its own key. */
const WORKER_NAMES: Record<string, string> = {
  calendar_mirror: 'Company calendar',
  calendar_sync: 'Calendar sync',
  news_digest: 'News round-up',
  'check-drive-links': 'Client link check',
}

const WORKER_CONSEQUENCE: Record<string, string> = {
  calendar_mirror:
    'The company calendar has stopped being copied in — the Calendar tab is showing what it last saw, not what is in Google now',
  calendar_sync:
    'Shoots and diary entries have stopped being written out to the company calendar — anything added in Portal OS since is not in Google',
  news_digest:
    'No round-up has been written — the News tab is showing older days, not today',
  'check-drive-links':
    'Nothing is watching the files behind our client links — a video that gets binned or unshared in Drive will now go unnoticed until a client says so',
}

export function workerName(worker: string): string {
  return WORKER_NAMES[worker] ?? worker.replace(/[_-]/g, ' ')
}

export function workerConsequence(worker: string): string {
  return WORKER_CONSEQUENCE[worker] ?? 'The scheduled task behind it has stopped running'
}
